// React
import { ReactElement } from 'react';
// Context
import { useQualifiers } from '@utmb/contexts/QualifiersContext';
// Modules
import { useTranslation } from 'next-i18next';
import debounce from 'lodash.debounce';
import ReactSelectFilter from '@utmb/features/react-select-filter/ReactSelectFilter';
// Interfaces
import { IconsEnum } from '@utmb/types/icons';
import { LangEnum } from '@utmb/types/enum/LangEnum';
// Utils
import { formatDate } from '@utmb/utils/intl';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
// Design System
import { DropDown } from '@utmb/design-system/dropdown/Dropdown';
import { Font } from '@utmb/design-system/text/font';
import { Icon } from '@utmb/design-system/icons/Icon';
import { ToolBar } from '@utmb/design-system/toolbar/Toolbar';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
import DateRangePicker from '@utmb/design-system/date-range-picker/DateRangePicker';
// Styles
import styles from './qualifiers-filters.module.scss';

export const QualifiersFilters = (): ReactElement => {
    const { t, i18n } = useTranslation();

    const {
        search,
        selectedCategories,
        selectedCountries,
        selectedDateMin,
        selectedDateMax,
        minDate,
        maxDate,
        filters: { categories, countries },
        handleCheckCategory,
        handleCheckCountry,
        handleCheckDate,
        handleChangeSearch,
    } = useQualifiers();

    const locale = (i18n.language as LangEnum) ?? LangEnum.EN;

    const isDefaultDateFilters = selectedDateMin === minDate && selectedDateMax === maxDate;

    const handleInputChange = async ({ target: { value } }: React.ChangeEvent<HTMLInputElement>): Promise<void> => handleChangeSearch(value);

    return (
        <div className="row">
            <div className="col-12">
                <ToolBar className={styles.toolbar}>
                    <DropDown
                        button={({ isExpanded }) => (
                            <>
                                <p className={styles.filter_label}>
                                    <Font.OxaniumRegular mobile="14">{t('qualifier.filters.category')}</Font.OxaniumRegular>
                                    <div className={styles.filter_content}>
                                        <Font.OxaniumRegular mobile="14" className={styles.filter_label_value}>
                                            {selectedCategories.map((category) => (
                                                <span key={category} className={styles.filter_label_icon}>
                                                    <PiCategoryLogo
                                                        height={20}
                                                        piCategory={categories.find((c) => c.value === category)!.value as PiCategoryEnum}
                                                    />
                                                </span>
                                            ))}
                                        </Font.OxaniumRegular>
                                        {selectedCategories.length > 0 ? (
                                            <button
                                                className={styles.reset_button}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    selectedCategories.map((category) => handleCheckCategory(category));
                                                }}
                                            >
                                                <Icon variant={IconsEnum.TRASH_SECOND} color={getColorStyle(ColorTypesEnum.RED)} />
                                            </button>
                                        ) : (
                                            '-'
                                        )}
                                    </div>
                                </p>
                                <Icon variant={IconsEnum[isExpanded ? 'CHEVRON_UP' : 'CHEVRON_DOWN']} />
                            </>
                        )}
                        className={`${styles.drop} ${styles.drop_country}`}
                    >
                        <ReactSelectFilter
                            options={categories}
                            selectedOptions={selectedCategories}
                            handleCheck={handleCheckCategory}
                            filterType={'category'}
                            placeholder={t('reactSelectFilter.filterCategory')}
                        />
                    </DropDown>

                    <DropDown
                        button={({ isExpanded }) => (
                            <>
                                <p className={styles.filter_label}>
                                    <Font.OxaniumRegular mobile="14">{t('qualifier.filters.country')}</Font.OxaniumRegular>
                                    <div className={styles.filter_content}>
                                        <Font.OxaniumRegular mobile="14" className={styles.filter_label_value}>
                                            {selectedCountries.map((countryCode) => countries.find((c) => c.value === countryCode)?.label).join(', ')}
                                        </Font.OxaniumRegular>
                                        {selectedCountries.length > 0 ? (
                                            <button
                                                className={styles.reset_button}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    selectedCountries.map((countryCode) => handleCheckCountry(countryCode));
                                                }}
                                            >
                                                <Icon variant={IconsEnum.TRASH_SECOND} color={getColorStyle(ColorTypesEnum.RED)} />
                                            </button>
                                        ) : (
                                            '-'
                                        )}
                                    </div>
                                </p>
                                <Icon variant={IconsEnum[isExpanded ? 'CHEVRON_UP' : 'CHEVRON_DOWN']} />
                            </>
                        )}
                        className={`${styles.drop} ${styles.drop_country}`}
                    >
                        <ReactSelectFilter
                            options={countries}
                            selectedOptions={selectedCountries}
                            handleCheck={handleCheckCountry}
                            filterType={'country'}
                            placeholder={t('reactSelectFilter.filterCountry')}
                        />
                    </DropDown>

                    <DropDown
                        button={({ isExpanded }) => (
                            <>
                                <p className={styles.filter_label}>
                                    <Font.OxaniumRegular mobile="14">{t('qualifier.filters.date')}</Font.OxaniumRegular>
                                    <div className={styles.filter_content}>
                                        <Font.OxaniumRegular mobile="14" className={styles.filter_label_value}>
                                            {formatDate(locale, selectedDateMin)} - {formatDate(locale, selectedDateMax)}
                                        </Font.OxaniumRegular>
                                        {!isDefaultDateFilters && (
                                            <button
                                                className={styles.reset_button}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCheckDate(minDate, maxDate);
                                                }}
                                            >
                                                <Icon variant={IconsEnum.TRASH_SECOND} color={getColorStyle(ColorTypesEnum.RED)} />
                                            </button>
                                        )}
                                    </div>
                                </p>
                                {isExpanded ? <Icon variant={IconsEnum.CHEVRON_UP} /> : <Icon variant={IconsEnum.CHEVRON_DOWN} />}
                            </>
                        )}
                        className={`${styles.drop} ${styles.drop_date}`}
                    >
                        <DateRangePicker
                            onChange={handleCheckDate}
                            locale={locale}
                            minDate={minDate}
                            maxDate={maxDate}
                            selectedDateMin={selectedDateMin}
                            selectedDateMax={selectedDateMax}
                        />
                    </DropDown>

                    <div className={styles.input_text}>
                        <input onChange={debounce(handleInputChange, 300)} type="search" placeholder={t('qualifier.filters.search')} defaultValue={search} />
                    </div>
                </ToolBar>
            </div>
        </div>
    );
};
